import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { aiAPI } from '../../utils/api';
import { formatDate } from '../../utils/timeFormat';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import LiveRateCalculator from '../../components/LiveRateCalculator';
import StandbyControls from '../../components/StandbyControls';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import AddNewCompanyModal from '../../components/AddNewCompanyModal';
import MultiRoleSlotsEditor, { emptyExtraSlot } from '../../components/MultiRoleSlotsEditor';
import { Switch } from '../../components/ui/switch';
import { ArrowLeft, Search, Loader2, CheckCircle, CalendarDays, Users as UsersIcon, Eye, Sparkles, Plus, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import EventTitleField from '../../components/EventTitleField';
import { formatApiError } from '../../utils/formatApiError';
import RadiusRecommendationCard from '../../components/RadiusRecommendationCard';
import PayoutTermsSelector from '../../components/PayoutTermsSelector';
import TierRoleCombobox from '../../components/TierRoleCombobox';
import { OnsiteDetailsFields } from '../../components/shift/OnsiteDetailsFields';

const AdminCreateShift = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill || null;
  const [roles, setRoles] = useState([]);
  const [clientResults, setClientResults] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Iter-225o — internal / direct-hire clients (e.g. iEvntür Internal)
  // loaded once so the shortcut button can render without a search hop.
  const [internalClients, setInternalClients] = useState([]);
  // Iter-297 — "Add New Company" modal state. Lets admins create a
  // stub client record on the fly when posting for an unsigned brand.
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  // Iter-297 — Extra staffing role slots for one-event-many-roles
  // events. Each entry triggers a separate shift under the same
  // event_group_id when the admin hits Create Job.
  const [extraRoleSlots, setExtraRoleSlots] = useState([]);

  const [form, setForm] = useState({
    role_id: prefill?.role_id || '',
    city: prefill?.city || '',
    state: prefill?.state || '',
    venue_name: prefill?.venue_name || '',
    venue_address: prefill?.venue_address || '',
    venue_lat: prefill?.venue_lat || null,
    venue_lng: prefill?.venue_lng || null,
    shift_date: prefill?.shift_date || '',  // blank for clone; pre-filled when coming from calendar date click
    end_date: '',
    is_multi_day: prefill?.is_multi_day || false,
    series_name: '',
    start_time: prefill?.start_time || '09:00',
    end_time: prefill?.end_time || '17:00',
    is_time_tbd: false,  // Iter-336
    is_date_tbd: false,  // 2026-06 Date TBD
    // Per-day time overrides for multi-day events
    customize_per_day: false,
    per_day_schedules: [],  // [{date:'YYYY-MM-DD', start_time:'HH:MM', end_time:'HH:MM'}]
    package_tier: prefill?.package_tier || 'hospitality',
    tier_required: prefill?.tier_required || 'flex',
    talent_needed: prefill?.talent_needed || 1,
    bill_rate: prefill?.bill_rate || 35,
    payout_type: prefill?.payout_type || 'standard',
    payout_terms_business_days: prefill?.payout_terms_business_days || 30,
    payout_expedited: prefill?.payout_expedited ?? false,
    payout_custom_note: prefill?.payout_custom_note || '',
    talent_pay_rate: prefill?.talent_pay_rate || '',
    pay_type: prefill?.pay_type || 'hourly',
    flat_pay: prefill?.flat_pay || '',
    talent_pay_rate_min: prefill?.talent_pay_rate_min || '',
    talent_pay_rate_max: prefill?.talent_pay_rate_max || '',
    duties: prefill?.duties || '',
    additional_notes: prefill?.additional_notes || '',
    important_notes: prefill?.important_notes || '',
    dress_code: prefill?.dress_code || '',
    confirmation_required_hours: prefill?.confirmation_required_hours ?? 24,
    cancellation_cutoff_hours: prefill?.cancellation_cutoff_hours ?? 24,
    // Backup staffing
    backups_needed: prefill?.backups_needed || 0,
    backup_pay_type: prefill?.backup_pay_type || 'flat',
    backup_flat_pay: prefill?.backup_flat_pay || '',
    backup_hourly_rate: prefill?.backup_hourly_rate || '',
    // Iter-190 — Standby / Floater Mode
    standby_enabled: prefill?.standby_enabled || false,
    standby_unlimited: prefill?.standby_unlimited || false,
    standby_limit: prefill?.standby_limit || '',
    standby_pay_amount: prefill?.standby_pay_amount || '',
    standby_hourly_rate: prefill?.standby_hourly_rate || '',
    standby_description: prefill?.standby_description || '',
    standby_notes: prefill?.standby_notes || '',
    standby_auto_promote: prefill?.standby_auto_promote !== false,
    // Visibility
    visibility_scope: prefill?.visibility_scope || 'local_70mi',
    visibility_radius_miles: prefill?.visibility_radius_miles || 70,
    // Iter-133 — auto-close on primary clock-out (default ON)
    auto_close_enabled: prefill?.auto_close_enabled !== false,
    // Iter-182 — public-facing event title.
    event_title: prefill?.event_title || '',
    // Iter-224f — Cost code (consolidation grouping)
    cost_code: prefill?.cost_code || '',
    // Iter-254 — Creator Network workflow
    creator_workflow: prefill?.creator_workflow || false,
    creator_type_required: prefill?.creator_type_required || '',
    // Iter-411m — GPS geofence radius (meters). NULL = server auto-recommends
    // via services/radius_recommender based on venue type + history.
    gps_radius_meters: prefill?.gps_radius_meters ?? null,
    // iter417/iter418 — structured on-site / arrival details (Evie reads these).
    booth_number: prefill?.booth_number || '',
    reporting_location: prefill?.reporting_location || '',
    entrance_instructions: prefill?.entrance_instructions || '',
    onsite_directions: prefill?.onsite_directions || '',
    parking_instructions: prefill?.parking_instructions || '',
    info_desk_location: prefill?.info_desk_location || '',
    arrival_instructions: prefill?.arrival_instructions || '',
    floor_plan_urls: prefill?.floor_plan_urls || [],
  });

  // Live audience preview — auto-updates when visibility fields change
  const [audience, setAudience] = useState({
    loading: false,
    count: null,
    sample: [],
    error: null,
  });

  // Iter-133p — admin booking parity. Curated dress code + attire +
  // grooming presets (mirrors what the client booking flow shows).
  const [curatedDressCodes, setCuratedDressCodes] = useState([]);
  const [curatedAttireRestrictions, setCuratedAttireRestrictions] = useState([]);
  const [curatedGroomingReqs, setCuratedGroomingReqs] = useState([]);
  const [selectedDressCode, setSelectedDressCode] = useState(null);
  const [attireRestrictions, setAttireRestrictions] = useState([]);
  const [groomingReqs, setGroomingReqs] = useState([]);
  const [sendHomeWarning, setSendHomeWarning] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);

  // Iter-133q — custom image + attachments uploaders
  const [customDressImages, setCustomDressImages] = useState([]);   // array of URLs already uploaded
  const [customGroomingImages, setCustomGroomingImages] = useState([]); // array of URLs
  const [uploadingDress, setUploadingDress] = useState(false);
  const [uploadingGrooming, setUploadingGrooming] = useState(false);
  const [queuedAttachments, setQueuedAttachments] = useState([]);   // [{file, visible_to_client, visible_to_talent}]

  // Iter-133r — AI dress code + important notes generation.
  const [aiDressGenerating, setAiDressGenerating] = useState(false);
  const [aiNotesGenerating, setAiNotesGenerating] = useState(false);
  const [aiDressText, setAiDressText] = useState('');   // editable preview the admin commits via "Apply"
  const [aiNotesText, setAiNotesText] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/presets/all');
        setCuratedDressCodes(res.data.dress_codes || []);
        setCuratedAttireRestrictions(res.data.attire_restrictions || []);
        setCuratedGroomingReqs(res.data.grooming_requirements || []);
      } catch (e) {
        console.error('Failed to load curated presets:', e);
      }
    })();
  }, []);

  // Iter-254 — Detect Creator Network roles (Influencer / Content Creator)
  // and auto-toggle creator_workflow. Admin can override the segment in the
  // sub-selector below the role dropdown.
  const _adminCreatorSlug = (() => {
    const r = roles.find(x => x.id === form.role_id);
    if (!r) return null;
    const n = String(r.name || '').trim().toLowerCase();
    if (n === 'influencer') return 'influencer';
    if (n === 'content creator') return 'content_creator';
    return null;
  })();
  const isAdminCreatorRole = !!_adminCreatorSlug;

  useEffect(() => {
    if (isAdminCreatorRole) {
      setForm(prev => ({
        ...prev,
        creator_workflow: true,
        creator_type_required: prev.creator_type_required || _adminCreatorSlug,
      }));
    } else {
      setForm(prev => ({ ...prev, creator_workflow: false, creator_type_required: '' }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminCreatorRole, _adminCreatorSlug]);

  const generateAIDescription = async () => {
    const currentRole = roles.find((r) => r.id === form.role_id);
    if (!currentRole) {
      toast.error('Please select a role first');
      return;
    }
    setAiGenerating(true);
    try {
      const response = await aiAPI.generateDescription({
        role_name: currentRole.name,
        venue_name: form.venue_name || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        custom_dress_code: selectedDressCode?.name || form.dress_code || undefined,
        talent_count: form.talent_needed || undefined,
      });
      const generated = response.data.description;
      update('duties', generated);
      toast.success('AI duties generated!');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to generate duties';
      toast.error(msg);
    } finally {
      setAiGenerating(false);
    }
  };

  // Iter-133q — image uploads for dress code + grooming examples.
  const uploadCustomImage = async (kind, files) => {
    if (!files?.length) return;
    const setUploading = kind === 'dress_code' ? setUploadingDress : setUploadingGrooming;
    const setList = kind === 'dress_code' ? setCustomDressImages : setCustomGroomingImages;
    const current = kind === 'dress_code' ? customDressImages : customGroomingImages;
    setUploading(true);
    try {
      const added = [];
      for (const f of Array.from(files)) {
        if (!f.type.startsWith('image/')) {
          toast.error(`${f.name}: not an image`);
          continue;
        }
        if (f.size > 5 * 1024 * 1024) {
          toast.error(`${f.name}: exceeds 5MB`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', f);
        const res = await api.post(`/admin/uploads/shift-image?kind=${kind}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        added.push(res.data.image_url);
      }
      if (added.length) {
        setList([...current, ...added]);
        toast.success(`${added.length} image${added.length === 1 ? '' : 's'} uploaded`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeCustomImage = (kind, url) => {
    if (kind === 'dress_code') {
      setCustomDressImages(customDressImages.filter((u) => u !== url));
    } else {
      setCustomGroomingImages(customGroomingImages.filter((u) => u !== url));
    }
  };

  const queueAttachment = (files) => {
    const fs = Array.from(files || []);
    const accepted = [];
    const skipped = [];
    for (const f of fs) {
      if (f.size > 15 * 1024 * 1024) {
        skipped.push(`${f.name} (>15MB)`);
        continue;
      }
      accepted.push({ file: f, visible_to_client: true, visible_to_talent: true });
    }
    if (skipped.length) toast.error(`Skipped: ${skipped.join(', ')}`);
    if (accepted.length) setQueuedAttachments([...queuedAttachments, ...accepted]);
  };

  const removeQueuedAttachment = (idx) => {
    setQueuedAttachments(queuedAttachments.filter((_, i) => i !== idx));
  };

  const toggleAttachmentVis = (idx, key) => {
    setQueuedAttachments(queuedAttachments.map((a, i) =>
      i === idx ? { ...a, [key]: !a[key] } : a,
    ));
  };

  // Iter-133r — AI generators for dress code + important notes.
  const _aiCtx = () => {
    const roleRow = roles.find((r) => r.id === form.role_id);
    return {
      role_name: roleRow?.name || undefined,
      client_name: selectedClient?.company_name || selectedClient?.name || undefined,
      event_type: form.venue_name ? 'Activation' : undefined,
      venue_name: form.venue_name || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
      duties: form.duties || undefined,
      talent_count: form.talent_needed || undefined,
    };
  };

  const generateAIDressCode = async () => {
    const ctx = _aiCtx();
    if (!ctx.role_name) { toast.error('Please select a role first'); return; }
    setAiDressGenerating(true);
    try {
      const res = await aiAPI.generateDressCode({ ...ctx, brand_tone: 'professional + friendly' });
      const text = res.data.dress_code_description || '';
      setAiDressText(text);
      update('dress_code', text); // paste straight in; admin can edit inline
      toast.success('AI dress code generated. Edit before saving if needed.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate dress code');
    } finally {
      setAiDressGenerating(false);
    }
  };

  const generateAIImportantNotes = async () => {
    const ctx = _aiCtx();
    if (!ctx.role_name) { toast.error('Please select a role first'); return; }
    setAiNotesGenerating(true);
    try {
      const res = await aiAPI.generateImportantNotes({
        ...ctx,
        dress_code_summary: selectedDressCode?.name || form.dress_code || undefined,
      });
      const text = res.data.important_notes || '';
      setAiNotesText(text);
      update('important_notes', text);
      toast.success('AI important notes generated. Edit before saving if needed.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate important notes');
    } finally {
      setAiNotesGenerating(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/roles');
        const rs = Array.isArray(res.data) ? res.data : (res.data.roles || []);
        setRoles(rs);
      } catch (e) { /* non-fatal */ }
      // Iter-225o — load the internal/direct-hire client(s) once so the
      // shortcut button can render immediately without a search round-trip.
      try {
        const ic = await api.get('/admin/clients/internal');
        setInternalClients(Array.isArray(ic.data) ? ic.data : []);
      } catch { /* non-fatal — fall back to manual search */ }

      // If a clone prefill is provided with client_id, fetch that client and auto-select
      if (prefill?.client_id) {
        try {
          const cs = await api.get('/admin/clients/search', { params: { q: '', limit: 100 } });
          const match = (cs.data || []).find((c) => c.id === prefill.client_id);
          if (match) setSelectedClient(match);
        } catch { /* non-fatal */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced client search
  useEffect(() => {
    if (selectedClient) return; // do not re-search after selection
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get('/admin/clients/search', { params: { q: clientSearch, limit: 20 } });
        setClientResults(res.data || []);
      } catch {
        setClientResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [clientSearch, selectedClient]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Build the list of dates (YYYY-MM-DD) between shift_date and end_date inclusive
  const buildDayList = (startStr, endStr) => {
    if (!startStr || !endStr) return [];
    const start = new Date(`${startStr}T00:00:00`);
    const end = new Date(`${endStr}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
    if (end < start) return [];
    const out = [];
    const cur = new Date(start);
    while (cur <= end) {
      out.push(cur.toISOString().slice(0, 10));
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  };

  // When the user toggles "Customize times per day" ON, seed per_day_schedules from the current date range
  // using the default start/end times. Also re-sync the list whenever the range or default times change
  // so newly-added days get seeded automatically. Unedited rows track the defaults; edited rows keep their own values.
  useEffect(() => {
    if (!form.is_multi_day || !form.customize_per_day) return;
    const days = buildDayList(form.shift_date, form.end_date);
    if (days.length === 0) {
      if (form.per_day_schedules.length !== 0) {
        setForm((f) => ({ ...f, per_day_schedules: [] }));
      }
      return;
    }
    setForm((f) => {
      const existingByDate = Object.fromEntries((f.per_day_schedules || []).map((e) => [e.date, e]));
      const next = days.map((d) => existingByDate[d] || {
        date: d,
        start_time: f.start_time,
        end_time: f.end_time,
      });
      // Only update if changed to avoid re-render loop
      const same = next.length === (f.per_day_schedules || []).length &&
        next.every((n, i) => {
          const p = f.per_day_schedules[i];
          return p && p.date === n.date && p.start_time === n.start_time && p.end_time === n.end_time;
        });
      return same ? f : { ...f, per_day_schedules: next };
    });
  }, [form.is_multi_day, form.customize_per_day, form.shift_date, form.end_date, form.start_time, form.end_time]);

  const updatePerDaySchedule = (idx, field, value) => {
    setForm((f) => {
      const next = [...f.per_day_schedules];
      next[idx] = { ...next[idx], [field]: value };
      return { ...f, per_day_schedules: next };
    });
  };

  const resetPerDayToDefault = () => {
    const days = buildDayList(form.shift_date, form.end_date);
    setForm((f) => ({
      ...f,
      per_day_schedules: days.map((d) => ({ date: d, start_time: f.start_time, end_time: f.end_time })),
    }));
    toast.success('Per-day times reset to default');
  };

  // Debounced live audience preview — fires whenever visibility-affecting fields change
  useEffect(() => {
    // Need minimum fields to get meaningful preview
    if (!form.state || !form.city) {
      setAudience({ loading: false, count: null, sample: [], error: null });
      return;
    }
    // For local/custom scope without coordinates, we can't compute radius properly
    const needsCoords = form.visibility_scope === 'local_70mi' || form.visibility_scope === 'custom_radius';
    if (needsCoords && (form.venue_lat == null || form.venue_lng == null)) {
      setAudience({ loading: false, count: null, sample: [], error: 'needs_address' });
      return;
    }

    let cancelled = false;
    setAudience((a) => ({ ...a, loading: true, error: null }));
    const t = setTimeout(async () => {
      try {
        const res = await api.post('/admin/audience-preview', {
          city: form.city,
          state: form.state,
          venue_lat: form.venue_lat,
          venue_lng: form.venue_lng,
          visibility_scope: form.visibility_scope,
          visibility_radius_miles: form.visibility_radius_miles,
          tier_required: form.tier_required,
        });
        if (cancelled) return;
        setAudience({
          loading: false,
          count: res.data.total_visible_to_see ?? 0,
          sample: res.data.sample_nearest || [],
          error: null,
        });
      } catch (e) {
        if (cancelled) return;
        setAudience({
          loading: false,
          count: null,
          sample: [],
          error: e.response?.data?.detail || 'Preview failed',
        });
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    form.city, form.state, form.venue_lat, form.venue_lng,
    form.visibility_scope, form.visibility_radius_miles, form.tier_required,
  ]);

  const submit = async () => {
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }
    if (!form.role_id || !form.shift_date || !form.city || !form.state || !form.venue_address) {
      toast.error('Fill in required fields: role, date, city, state, venue address');
      return;
    }
    if (form.is_multi_day) {
      if (!form.end_date) {
        toast.error('Multi-day event requires an End Date');
        return;
      }
      if (form.end_date < form.shift_date) {
        toast.error('End Date must be on or after the Start Date');
        return;
      }
    }
    if ((form.backups_needed || 0) > 0) {
      if (form.backup_pay_type === 'flat' && !form.backup_flat_pay) {
        toast.error('Backup Flat Pay is required');
        return;
      }
      if (form.backup_pay_type === 'hourly' && !form.backup_hourly_rate) {
        toast.error('Backup Hourly Rate is required');
        return;
      }
    }
    if (form.pay_type === 'flat' && (!form.flat_pay || parseFloat(form.flat_pay) <= 0)) {
      toast.error('Flat Pay total is required for a flat-rate gig');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        client_id: selectedClient.id,
        from_call_id: prefill?.from_call_id || undefined,
        bill_rate: parseFloat(form.bill_rate),
        pay_type: form.pay_type === 'flat' ? 'flat' : 'hourly',
        flat_pay: form.pay_type === 'flat' && form.flat_pay !== '' && form.flat_pay !== null
          ? parseFloat(form.flat_pay) : null,
        talent_pay_rate: form.talent_pay_rate === '' || form.talent_pay_rate === null
          ? null : parseFloat(form.talent_pay_rate),
        talent_pay_rate_min: form.talent_pay_rate_min === '' || form.talent_pay_rate_min === null
          ? null : parseFloat(form.talent_pay_rate_min),
        talent_pay_rate_max: form.talent_pay_rate_max === '' || form.talent_pay_rate_max === null
          ? null : parseFloat(form.talent_pay_rate_max),
        talent_needed: parseInt(form.talent_needed, 10),
        backups_needed: parseInt(form.backups_needed || 0, 10),
        backup_flat_pay: form.backup_flat_pay ? parseFloat(form.backup_flat_pay) : null,
        backup_hourly_rate: form.backup_hourly_rate ? parseFloat(form.backup_hourly_rate) : null,
        // Iter-190 — Standby / Floater Mode payload
        standby_enabled: !!form.standby_enabled,
        standby_unlimited: !!form.standby_unlimited,
        standby_limit: form.standby_limit && !form.standby_unlimited ? parseInt(form.standby_limit, 10) : null,
        standby_pay_amount: form.standby_pay_amount ? parseFloat(form.standby_pay_amount) : null,
        standby_hourly_rate: form.standby_hourly_rate ? parseFloat(form.standby_hourly_rate) : null,
        standby_description: form.standby_description || null,
        standby_notes: form.standby_notes || null,
        standby_auto_promote: form.standby_auto_promote !== false,
        // Iter-254 — Creator Network workflow flags.
        creator_workflow: !!form.creator_workflow,
        creator_type_required: form.creator_workflow ? (form.creator_type_required || null) : null,
        // Iter-411m — Optional GPS geofence radius override. NULL lets the
        // server auto-recommend based on venue type + historical spread.
        gps_radius_meters: form.gps_radius_meters == null || form.gps_radius_meters === ''
          ? null : parseInt(form.gps_radius_meters, 10),
      };
      // Normalize backup pay fields based on pay type
      if ((payload.backups_needed || 0) === 0) {
        payload.backup_pay_type = null;
        payload.backup_flat_pay = null;
        payload.backup_hourly_rate = null;
      } else if (payload.backup_pay_type === 'flat') {
        payload.backup_hourly_rate = null;
      } else if (payload.backup_pay_type === 'hourly') {
        payload.backup_flat_pay = null;
      }
      // Single-day cleanup
      if (!form.is_multi_day) {
        payload.end_date = null;
        payload.series_name = null;
        payload.per_day_schedules = null;
      } else {
        // Multi-day: only send per_day_schedules when the user explicitly enabled customization
        // AND at least one day differs from the default. Send full list so backend can override any day.
        if (form.customize_per_day && (form.per_day_schedules || []).length > 0) {
          const defS = form.start_time;
          const defE = form.end_time;
          const anyCustom = form.per_day_schedules.some((e) => e.start_time !== defS || e.end_time !== defE);
          payload.per_day_schedules = anyCustom ? form.per_day_schedules : null;
        } else {
          payload.per_day_schedules = null;
        }
      }
      // Iter-336 — TBD + customize_per_day handling. Backend accepts:
      //   - is_time_tbd=true → start/end null OK
      //   - customize_per_day=true → per-day schedules are source of truth
      const perDayOn = !!(form.is_multi_day && form.customize_per_day);
      if (form.is_time_tbd) {
        payload.start_time = null;
        payload.end_time = null;
      }
      payload.is_time_tbd = !!form.is_time_tbd;
      payload.is_date_tbd = !!form.is_date_tbd;
      payload.customize_per_day = perDayOn;
      // If per-day is ON, don't require top-level times either.
      if (perDayOn && !form.is_time_tbd) {
        // Keep top-level times as fallback for days not overridden — nothing to strip.
      }
      // UI-only fields that backend doesn't accept
      delete payload.customize_per_day_ui;  // (no-op — kept for future-proofing)
      if (!form.important_notes?.trim()) delete payload.important_notes;
      if (payload.talent_pay_rate === null) delete payload.talent_pay_rate;
      if (payload.talent_pay_rate_min === null) delete payload.talent_pay_rate_min;
      if (payload.talent_pay_rate_max === null) delete payload.talent_pay_rate_max;

      // Iter-133p — admin booking parity. Curated dress code + attire +
      // grooming + send-home toggle.
      payload.dress_code_name = selectedDressCode?.name || null;
      payload.dress_code_checklist = selectedDressCode?.checklist || [];
      payload.dress_code_description = selectedDressCode?.description || null;
      // Combine preset image (if any) with custom uploads.
      payload.dress_code_images = [
        ...(selectedDressCode?.image_url ? [selectedDressCode.image_url] : []),
        ...customDressImages,
      ];
      payload.attire_restrictions = attireRestrictions;
      payload.grooming_requirements = groomingReqs;
      payload.grooming_images = customGroomingImages;
      payload.send_home_warning = !!sendHomeWarning;

      const res = await api.post('/admin/shifts', payload);
      const shiftId = res.data?.id || res.data?.shift_id;

      // Iter-297 — If admin attached additional role slots, fan out
      // each one as a sibling shift sharing the same event_group_id.
      // We do this AFTER the primary shift succeeds so any single-row
      // validation errors surface on the primary form (where the admin
      // is already focused) before we start spawning extras.
      let extrasShiftIds = [];
      let extrasFailed = [];
      if (extraRoleSlots.length > 0 && res.data?.event_group_id == null) {
        // Backfill event_group_id on the primary shift, then create
        // siblings. We do this client-side via the multi-role endpoint
        // for extras only — the primary shift was already created by
        // the legacy single-shift endpoint above and shares the same
        // event group via the patch below.
        try {
          const eventGroupRes = await api.post(`/admin/shifts/${shiftId}/attach-group`);
          const groupId = eventGroupRes.data?.event_group_id;
          for (let i = 0; i < extraRoleSlots.length; i += 1) {
            const slot = extraRoleSlots[i];
            const slotPayload = {
              client_id: selectedClient.id,
              event_group_id: groupId,
              role_id: slot.role_id,
              city: payload.city,
              state: payload.state,
              venue_name: payload.venue_name,
              venue_address: payload.venue_address,
              venue_lat: payload.venue_lat,
              venue_lng: payload.venue_lng,
              shift_date: payload.shift_date,
              end_date: payload.end_date,
              start_time: slot.start_time || payload.start_time,
              end_time: slot.end_time || payload.end_time,
              package_tier: slot.package_tier || payload.package_tier,
              tier_required: 'flex',
              talent_needed: parseInt(slot.talent_needed || 1, 10),
              bill_rate: parseFloat(slot.bill_rate || 0),
              talent_pay_rate: slot.talent_pay_rate
                ? parseFloat(slot.talent_pay_rate)
                : undefined,
              dress_code: slot.dress_code || null,
              additional_notes: [
                slot.additional_notes,
                slot.certifications
                  ? `Required certifications: ${slot.certifications}`
                  : null,
              ].filter(Boolean).join('\n\n') || null,
              event_title: payload.event_title || null,
              visibility_scope: payload.visibility_scope,
              visibility_radius_miles: payload.visibility_radius_miles,
              confirmation_required_hours: payload.confirmation_required_hours,
              cancellation_cutoff_hours: payload.cancellation_cutoff_hours,
              auto_close_enabled: payload.auto_close_enabled,
              // 2026-06 — sibling roles inherit the event's payout terms
              payout_type: payload.payout_type,
              payout_terms_business_days: payload.payout_terms_business_days,
              payout_expedited: payload.payout_expedited,
              payout_custom_note: payload.payout_custom_note,
              // iter435 — sibling roles inherit the event's compensation model
              pay_type: payload.pay_type,
              flat_pay: payload.flat_pay,
            };
            try {
              const sr = await api.post('/admin/shifts', slotPayload);
              extrasShiftIds.push(sr.data?.id || sr.data?.shift_id);
            } catch (slotErr) {
              extrasFailed.push({
                index: i,
                role_id: slot.role_id,
                reason: slotErr?.response?.data?.detail || 'send failed',
              });
            }
          }
        } catch (groupErr) {
          // If we can't attach the group, fall back to leaving extras
          // unsaved so the admin can fix and resubmit.
          toast.error('Created primary shift but could not link extras: ' + (groupErr?.response?.data?.detail || groupErr.message));
        }
      }

      // Iter-133q — upload queued attachments against the new shift id.
      if (shiftId && queuedAttachments.length > 0) {
        let okCount = 0;
        for (const att of queuedAttachments) {
          try {
            const fd = new FormData();
            fd.append('file', att.file);
            await api.post(
              `/admin/shifts/${shiftId}/attachments?visible_to_client=${att.visible_to_client}&visible_to_talent=${att.visible_to_talent}`,
              fd,
              { headers: { 'Content-Type': 'multipart/form-data' } },
            );
            okCount += 1;
          } catch (err) {
            toast.error(`Failed to upload ${att.file.name}: ${err?.response?.data?.detail || err.message}`);
          }
        }
        if (okCount > 0) toast.success(`${okCount} attachment${okCount === 1 ? '' : 's'} uploaded`);
      }
      const totalCreated = (res.data.total_shifts_created || 1) + extrasShiftIds.length;
      if (extraRoleSlots.length > 0) {
        if (extrasFailed.length === 0) {
          toast.success(
            `Multi-role event created — ${totalCreated} linked shift${totalCreated === 1 ? '' : 's'} under one event group.`
          );
        } else {
          toast.warning(
            `Primary shift created. ${extrasShiftIds.length}/${extraRoleSlots.length} additional roles attached; ${extrasFailed.length} failed — open the shift to retry.`
          );
        }
      } else {
        toast.success(
          totalCreated > 1
            ? `Multi-day event created — ${totalCreated} shifts linked under one series`
            : 'Job created successfully'
        );
      }
      navigate(`/admin/shifts/${res.data.id}?invite=1`);
    } catch (e) {
      // Iter-411f — surface the real backend detail (string, Pydantic 422 array,
      // {code,message} dict, or network-level error) instead of a flat
      // "Failed to create shift" that leaves admins guessing.
      const msg = formatApiError(e, 'Failed to create shift');
      toast.error(msg, { duration: 10000 });
      console.error('[create shift] backend responded with:', e?.response?.status, e?.response?.data);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-create-shift-page">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/admin/shifts')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Shifts
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Job / Shift</h1>
          <p className="text-sm text-gray-500 mt-1">Manually post a job on behalf of a client.</p>
        </div>

        {/* CLIENT SELECTOR */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">1. Select Client</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {selectedClient ? (
              <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-200" data-testid="selected-client">
                <div>
                  <div className="font-semibold text-green-900 flex items-center gap-2 flex-wrap">
                    <CheckCircle className="w-4 h-4" />
                    {selectedClient.display}
                    {selectedClient.is_internal && (
                      <span
                        className="ml-1 inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700 border border-indigo-200"
                        data-testid="internal-client-badge"
                      >
                        Internal · Direct Booking
                      </span>
                    )}
                    {selectedClient.pending_activation && (
                      <span
                        className="ml-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 border border-amber-200"
                        data-testid="stub-client-badge"
                      >
                        <Mail className="w-2.5 h-2.5" />
                        Stub · Awaiting invite
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-green-700">{selectedClient.email}</div>
                </div>
                <Button variant="outline" size="sm" onClick={() => { setSelectedClient(null); setClientSearch(''); }} data-testid="change-client-btn">
                  Change
                </Button>
              </div>
            ) : (
              <>
                {/* Iter-225o — Direct Hire shortcut. Lets admins one-click
                    post for iEvntür itself without faking an external client. */}
                {internalClients.length > 0 && (
                  <div
                    className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 flex flex-wrap items-center gap-2"
                    data-testid="direct-hire-shortcut"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-indigo-900">
                        Posting a Direct Booking role for iEvnt&uuml;r?
                      </div>
                      <div className="text-xs text-indigo-700/80 mt-0.5">
                        Skip the external client picker — this attributes the shift to your internal company.
                      </div>
                    </div>
                    {internalClients.map((ic) => (
                      <Button
                        key={ic.id}
                        type="button"
                        size="sm"
                        onClick={() => setSelectedClient(ic)}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        data-testid={`select-internal-client-${ic.id}`}
                      >
                        Post for {ic.company_name || 'iEvntür Internal'}
                      </Button>
                    ))}
                  </div>
                )}

                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    placeholder="Search company name, contact, or email..."
                    className="pl-9"
                    data-testid="client-search-input"
                  />
                </div>
                {/* Iter-297 — Add-new-company entry point. Sits flush
                    with the search input so admins can fall through
                    when a brand hasn't onboarded yet. */}
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] text-gray-500">
                    Don&apos;t see them? Create the company now — no invite is sent.
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setAddCompanyOpen(true)}
                    className="text-cyan-700 border-cyan-200 hover:bg-cyan-50"
                    data-testid="add-new-company-btn"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    Add New Company
                  </Button>
                </div>
                <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                  {searching && (
                    <div className="p-4 text-center text-gray-400 text-sm">Searching…</div>
                  )}
                  {!searching && clientResults.length === 0 && (
                    <div className="p-4 text-center text-gray-400 text-sm">No clients found.</div>
                  )}
                  {!searching && clientResults.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedClient(c)}
                      className="w-full text-left px-4 py-2 hover:bg-cyan-50 transition"
                      data-testid="client-option"
                    >
                      <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                        {c.display}
                        {c.is_internal && (
                          <span className="inline-flex items-center rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700 border border-indigo-200">
                            Internal
                          </span>
                        )}
                        {c.pending_activation && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 border border-amber-200"
                            title="Stub company — not yet invited to register"
                          >
                            <Mail className="w-2.5 h-2.5" />
                            Stub
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">{c.email}{c.phone ? ` · ${c.phone}` : ''}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* SHIFT DETAILS */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">2. Job Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Iter-182 — Event Title field (public-facing) */}
            <EventTitleField
              value={form.event_title}
              onChange={(v) => update('event_title', v)}
              role_name={roles.find(r => r.id === form.role_id)?.name}
              event_type={form.package_tier}
              venue_name={form.venue_name}
              city={form.city}
              state={form.state}
              notes={form.additional_notes}
            />
            {/* Iter-224f — Optional cost code. Drives multi-day invoice consolidation:
                same client + event + venue + city + cost_code + consecutive dates
                → ONE invoice. Leave blank = group by role (default). */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Cost Code
                <span className="text-xs text-gray-500 font-normal">(optional · invoice grouping)</span>
              </Label>
              <Input
                value={form.cost_code}
                onChange={(e) => update('cost_code', e.target.value)}
                placeholder="e.g. MKT-2026-Q3 or EVT-DALLAS-HOME-SHOW"
                maxLength={64}
                data-testid="shift-cost-code-input"
              />
              <p className="text-xs text-gray-500">
                Used for multi-day invoice consolidation. Shifts with the same
                client, event, venue, city, and cost code on consecutive dates
                roll up into one invoice. Blank = falls back to role.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 space-y-2">
                <TierRoleCombobox
                  value={form.role_id}
                  context="client_booking"
                  testid="role-select"
                  onChange={(roleId, role) => {
                    // Set role and auto-align billing fold to prevent conflicting tier/role rates.
                    setForm((prev) => ({ ...prev, role_id: roleId || '',
                      package_tier: role?.billing_fold || prev.package_tier }));
                  }}
                />
              </div>
              {isAdminCreatorRole && (
                <div className="md:col-span-2 space-y-2 bg-purple-50 border border-purple-200 rounded-lg p-4" data-testid="admin-creator-workflow-block">
                  <Label className="text-purple-900 font-semibold">Creator Network · pick the segment</Label>
                  <p className="text-xs text-purple-800/80">Only vetted, verified creators are notified.</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { v: 'influencer', label: 'Influencers', sub: 'Audience-driven' },
                      { v: 'content_creator', label: 'Content Creators', sub: 'UGC / photo / video' },
                      { v: 'both', label: 'Influencer + Creator', sub: 'Hybrid talent' },
                    ].map((opt) => (
                      <button
                        type="button"
                        key={opt.v}
                        onClick={() => update('creator_type_required', opt.v)}
                        data-testid={`admin-creator-type-${opt.v}`}
                        className={
                          'text-left rounded-lg border-2 px-3 py-2 transition ' +
                          (form.creator_type_required === opt.v
                            ? 'border-purple-500 bg-white shadow-sm'
                            : 'border-purple-200 bg-white hover:border-purple-300')
                        }
                      >
                        <div className="text-sm font-semibold text-purple-900">{opt.label}</div>
                        <div className="text-[11px] text-gray-500 leading-snug">{opt.sub}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input type="date" value={form.shift_date} onChange={(e) => update('shift_date', e.target.value)} data-testid="date-input" />
              </div>
              <div className="space-y-2">
                <Label>Talent Needed *</Label>
                <Input type="number" min="1" value={form.talent_needed} onChange={(e) => update('talent_needed', e.target.value)} data-testid="staff-count-input" />
              </div>

              {/* MULTI-DAY TOGGLE */}
              <div className="md:col-span-2 rounded-lg border border-cyan-200 bg-cyan-50/40 p-3 sm:p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Label className="flex items-center gap-2 font-semibold text-gray-900">
                      <CalendarDays className="w-4 h-4 text-cyan-600" />
                      Multi-Day Event
                    </Label>
                    <p className="text-xs text-gray-500 mt-1">
                      Create one linked shift per day. Attendance, invoices, and payouts track per day.
                    </p>
                  </div>
                  <Switch
                    checked={form.is_multi_day}
                    onCheckedChange={(v) => update('is_multi_day', v)}
                    data-testid="multi-day-toggle"
                  />
                </div>
                {form.is_multi_day && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                    <div className="space-y-2">
                      <Label>End Date *</Label>
                      <Input
                        type="date"
                        value={form.end_date}
                        min={form.shift_date || undefined}
                        onChange={(e) => update('end_date', e.target.value)}
                        data-testid="end-date-input"
                      />
                      <p className="text-[11px] text-gray-500">
                        {form.shift_date && form.end_date
                          ? (() => {
                              const d1 = new Date(form.shift_date);
                              const d2 = new Date(form.end_date);
                              const days = Math.max(1, Math.round((d2 - d1) / 86400000) + 1);
                              return `${days} day${days === 1 ? '' : 's'} · ${form.talent_needed || 1} talent/day = ${days * (form.talent_needed || 1)} total shifts`;
                            })()
                          : 'Pick an end date to see the day count'}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Series Name <span className="text-xs text-gray-400">(optional)</span></Label>
                      <Input
                        value={form.series_name}
                        onChange={(e) => update('series_name', e.target.value)}
                        placeholder="e.g. Art Basel Activation"
                        data-testid="series-name-input"
                      />
                    </div>

                    {/* Customize times per day — collapsible */}
                    <div className="md:col-span-2 rounded-md border border-cyan-200 bg-white/70 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <Label className="font-medium text-gray-900">
                            Customize times per day
                          </Label>
                          <p className="text-[11px] text-gray-500 mt-0.5">
                            By default every day uses the Start / End Time below. Turn this on to set different hours for specific dates.
                          </p>
                        </div>
                        <Switch
                          checked={form.customize_per_day}
                          onCheckedChange={(v) => update('customize_per_day', v)}
                          data-testid="customize-per-day-toggle"
                        />
                      </div>

                      {form.customize_per_day && form.end_date && form.shift_date && form.per_day_schedules.length > 0 && (
                        <div className="mt-3 space-y-2" data-testid="per-day-schedule-list">
                          <div className="flex items-center justify-between">
                            <p className="text-[11px] font-medium text-gray-600 uppercase tracking-wide">
                              Per-day schedule ({form.per_day_schedules.length} day{form.per_day_schedules.length === 1 ? '' : 's'})
                            </p>
                            <button
                              type="button"
                              onClick={resetPerDayToDefault}
                              className="text-[11px] text-cyan-600 hover:text-cyan-800 underline"
                              data-testid="reset-per-day-btn"
                            >
                              Reset to default
                            </button>
                          </div>
                          <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
                            {form.per_day_schedules.map((entry, idx) => {
                              const dayLabel = formatDate(entry.date);
                              const isOvernight = entry.end_time && entry.start_time && entry.end_time <= entry.start_time;
                              return (
                                <div
                                  key={entry.date}
                                  className="grid grid-cols-[1fr_auto_auto] gap-2 items-center bg-gray-50 rounded-md px-2 py-1.5"
                                  data-testid={`per-day-row-${entry.date}`}
                                >
                                  <div className="flex flex-col">
                                    <span className="text-xs font-medium text-gray-800">{dayLabel}</span>
                                    <span className="text-[10px] text-gray-400">{entry.date}</span>
                                    {isOvernight && (
                                      <span className="text-[10px] text-amber-600 mt-0.5">Rolls over to next day</span>
                                    )}
                                  </div>
                                  <Input
                                    type="time"
                                    value={entry.start_time}
                                    onChange={(e) => updatePerDaySchedule(idx, 'start_time', e.target.value)}
                                    className="h-8 text-sm w-[110px]"
                                    data-testid={`per-day-start-${entry.date}`}
                                  />
                                  <Input
                                    type="time"
                                    value={entry.end_time}
                                    onChange={(e) => updatePerDaySchedule(idx, 'end_time', e.target.value)}
                                    className="h-8 text-sm w-[110px]"
                                    data-testid={`per-day-end-${entry.date}`}
                                  />
                                </div>
                              );
                            })}
                          </div>
                          <p className="text-[10px] text-gray-400 mt-1">
                            Tip: end times earlier than start times roll over to the next day (overnight shifts).
                          </p>
                        </div>
                      )}

                      {form.customize_per_day && (!form.shift_date || !form.end_date) && (
                        <p className="text-[11px] text-amber-600 mt-2">
                          Pick a Start Date and End Date above to list the days.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Iter-336 — TBD toggle + auto-disable when Customize-per-day is ON */}
              {(() => {
                const perDayOn = !!(form.is_multi_day && form.customize_per_day);
                const disableTop = !!form.is_time_tbd || perDayOn;
                return (
                  <>
                    <div className="md:col-span-2 flex flex-wrap items-center gap-3 text-sm">
                      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={!!form.is_time_tbd}
                          disabled={perDayOn}
                          onChange={(e) => {
                            update('is_time_tbd', e.target.checked);
                            if (e.target.checked) { update('start_time', ''); update('end_time', ''); }
                          }}
                          data-testid="tbd-toggle"
                        />
                        <span>Time TBD (times not decided yet)</span>
                      </label>
                      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={!!form.is_date_tbd}
                          onChange={(e) => update('is_date_tbd', e.target.checked)}
                          data-testid="date-tbd-toggle"
                        />
                        <span>Date TBD (date not decided yet)</span>
                      </label>
                      {perDayOn && (
                        <span className="text-xs text-cyan-700">
                          Using per-day schedule — general times disabled.
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className={disableTop ? 'opacity-60' : ''}>Start Time{disableTop ? '' : ' *'}</Label>
                      <Input type="time" value={form.start_time} disabled={disableTop} onChange={(e) => update('start_time', e.target.value)} className="disabled:bg-gray-100 disabled:text-gray-400" data-testid="start-time-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className={disableTop ? 'opacity-60' : ''}>End Time{disableTop ? '' : ' *'}</Label>
                      <Input type="time" value={form.end_time} disabled={disableTop} onChange={(e) => update('end_time', e.target.value)} className="disabled:bg-gray-100 disabled:text-gray-400" data-testid="end-time-input" />
                    </div>
                  </>
                );
              })()}
              <div className="space-y-2 md:col-span-2">
                <Label>Venue Name <span className="text-xs text-gray-400">(type a landmark / business — Google auto-fills the address)</span></Label>
                <AddressAutocomplete
                  value={form.venue_name}
                  onChange={(v) => update('venue_name', v)}
                  onSelect={(place) => {
                    if (!place) return;
                    setForm((f) => ({
                      ...f,
                      venue_name: place.name || f.venue_name,
                      venue_address: place.address || f.venue_address,
                      city: place.city || f.city,
                      state: place.state_code || place.state || f.state,
                      venue_lat: place.lat || null,
                      venue_lng: place.lng || null,
                    }));
                  }}
                  searchType="establishment"
                  placeholder="e.g. Nashville Fairgrounds, Bryant Park, Rooftop Lounge…"
                  country="us"
                  data-testid="venue-name-autocomplete"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Venue Address * <span className="text-xs text-gray-400">(Google Maps autofills city, state, zip)</span></Label>
                <AddressAutocomplete
                  value={form.venue_address}
                  onChange={(v) => update('venue_address', v)}
                  onSelect={(place) => {
                    if (!place) return;
                    setForm((f) => ({
                      ...f,
                      venue_address: place.address || f.venue_address,
                      city: place.city || f.city,
                      state: place.state_code || place.state || f.state,
                      venue_lat: place.lat || null,
                      venue_lng: place.lng || null,
                    }));
                  }}
                  placeholder="Start typing address — suggestions will appear"
                  country="us"
                  data-testid="venue-address-autocomplete"
                />
              </div>
              <RadiusRecommendationCard
                value={form.gps_radius_meters}
                onChange={(v) => update('gps_radius_meters', v)}
                venue_name={form.venue_name}
                venue_address={form.venue_address}
                city={form.city}
                state={form.state}
                venue_lat={form.venue_lat}
                venue_lng={form.venue_lng}
              />
              <div className="space-y-2">
                <Label>City *</Label>
                <Input value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="Miami" data-testid="city-input" />
              </div>
              <div className="space-y-2">
                <Label>State *</Label>
                <Input value={form.state} onChange={(e) => update('state', e.target.value)} placeholder="FL" maxLength={2} data-testid="state-input" />
              </div>
              <div className="space-y-2">
                <Label>Bill Rate ($ / hr) *</Label>
                <Input type="number" step="0.01" value={form.bill_rate} onChange={(e) => update('bill_rate', e.target.value)} data-testid="bill-rate-input" />
                <p className="text-[11px] text-gray-400">What the client pays you per hour.</p>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Compensation type *</Label>
                <div className="flex gap-2" data-testid="pay-type-toggle">
                  {[['hourly', 'Hourly rate'], ['flat', 'Flat rate']].map(([val, lbl]) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => update('pay_type', val)}
                      data-testid={`pay-type-${val}`}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${form.pay_type === val ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-white text-gray-600 border-gray-300'}`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400">
                  {form.pay_type === 'flat'
                    ? 'Talent is paid one fixed total for the assignment, regardless of hours worked.'
                    : 'Talent is paid per hour worked (overtime rules apply).'}
                </p>
              </div>
              {form.pay_type === 'flat' && (
                <div className="space-y-2 md:col-span-2">
                  <Label>Flat Pay — total $ *</Label>
                  <Input
                    type="number" step="0.01"
                    value={form.flat_pay}
                    onChange={(e) => update('flat_pay', e.target.value)}
                    placeholder="e.g. 250.00"
                    data-testid="flat-pay-input"
                  />
                  <p className="text-[11px] text-gray-400">Total agreed compensation per talent for the whole assignment.</p>
                </div>
              )}
              {form.pay_type !== 'flat' && (
              <div className="space-y-2">
                <Label>Talent Pay Rate ($ / hr) <span className="text-xs text-gray-400">(default — auto-calc if blank)</span></Label>
                <Input
                  type="number" step="0.01"
                  value={form.talent_pay_rate}
                  onChange={(e) => update('talent_pay_rate', e.target.value)}
                  placeholder="Auto-calculated from platform margin"
                  data-testid="talent-pay-rate-input"
                />
                <p className="text-[11px] text-gray-400">Default pay used when admin doesn't set a per-talent rate.</p>
              </div>
              )}
              {form.pay_type !== 'flat' && (
              <div className="space-y-2 md:col-span-2">
                <Label className="flex items-center gap-1.5">
                  Pay Rate Range <span className="text-xs text-gray-400">(optional — for experience-based per-talent rates)</span>
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    type="number" step="0.01"
                    value={form.talent_pay_rate_min}
                    onChange={(e) => update('talent_pay_rate_min', e.target.value)}
                    placeholder="Min ($/hr)"
                    data-testid="talent-pay-rate-min-input"
                  />
                  <Input
                    type="number" step="0.01"
                    value={form.talent_pay_rate_max}
                    onChange={(e) => update('talent_pay_rate_max', e.target.value)}
                    placeholder="Max ($/hr)"
                    data-testid="talent-pay-rate-max-input"
                  />
                </div>
                <p className="text-[11px] text-gray-400">e.g. $25 – $32. When assigning talent, you can pick a specific rate within this band based on experience or reliability.</p>
              </div>
              )}
              <div className="space-y-2 sm:col-span-2 border-t pt-4 mt-1">
                <PayoutTermsSelector
                  allowCustom
                  value={{
                    payout_type: form.payout_type,
                    payout_terms_business_days: form.payout_terms_business_days,
                    payout_expedited: form.payout_expedited,
                    payout_custom_note: form.payout_custom_note,
                  }}
                  onChange={(nv) => setForm((f) => ({ ...f, ...nv }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Tier Required</Label>
                <Select value={form.tier_required} onValueChange={(v) => update('tier_required', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flex">Flex</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Iter-309 — Additional Staffing Roles sits between
                  Primary Role headcount and Backup Staffing. */}
              <div className="md:col-span-2 rounded-lg border border-cyan-200 bg-cyan-50/40 p-3 sm:p-4" data-testid="additional-roles-block">
                <MultiRoleSlotsEditor
                  slots={extraRoleSlots}
                  onChange={setExtraRoleSlots}
                  roles={roles}
                  primaryRoleId={form.role_id}
                  primaryStartTime={form.start_time}
                  primaryEndTime={form.end_time}
                  isMultiDay={!!form.is_multi_day}
                  eventStartDate={form.shift_date}
                  eventEndDate={form.end_date}
                />
              </div>

              {/* BACKUP STAFFING */}
              <div className="md:col-span-2 rounded-lg border border-amber-200 bg-amber-50/40 p-3 sm:p-4">
                <Label className="flex items-center gap-2 font-semibold text-gray-900 mb-3">
                  <UsersIcon className="w-4 h-4 text-amber-600" />
                  Backup Staffing <span className="text-xs font-normal text-gray-500">(standby talent — paid only if used OR flat standby applies)</span>
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Backups Needed</Label>
                    <Input
                      type="number" min="0" max="20"
                      value={form.backups_needed}
                      onChange={(e) => update('backups_needed', e.target.value)}
                      data-testid="backups-needed-input"
                    />
                  </div>
                  {(form.backups_needed || 0) > 0 && (
                    <>
                      <div className="space-y-2">
                        <Label className="text-xs">Pay Type</Label>
                        <Select
                          value={form.backup_pay_type}
                          onValueChange={(v) => update('backup_pay_type', v)}
                        >
                          <SelectTrigger data-testid="backup-pay-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="flat">Flat Rate</SelectItem>
                            <SelectItem value="hourly">Hourly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        {form.backup_pay_type === 'flat' ? (
                          <>
                            <Label className="text-xs">Backup Flat Pay ($)</Label>
                            <Input
                              type="number" step="0.01" min="0"
                              value={form.backup_flat_pay}
                              onChange={(e) => update('backup_flat_pay', e.target.value)}
                              placeholder="e.g. 50.00"
                              data-testid="backup-flat-pay-input"
                            />
                          </>
                        ) : (
                          <>
                            <Label className="text-xs">Backup Hourly Rate ($/hr)</Label>
                            <Input
                              type="number" step="0.01" min="0"
                              value={form.backup_hourly_rate}
                              onChange={(e) => update('backup_hourly_rate', e.target.value)}
                              placeholder="e.g. 20.00"
                              data-testid="backup-hourly-rate-input"
                            />
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
              {/* Iter-190 — Standby / Floater Mode */}
              <StandbyControls form={form} update={update} />
              <div className="space-y-2 md:col-span-2">
                <div className="flex items-center justify-between">
                  <Label>Duties <span className="text-xs text-cyan-700 font-normal ml-2">AI-Powered</span></Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generateAIDescription}
                    disabled={aiGenerating || !form.role_id}
                    data-testid="admin-generate-duties-btn"
                  >
                    {aiGenerating ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Generating…</>
                    ) : (
                      <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Generate with AI</>
                    )}
                  </Button>
                </div>
                <Textarea
                  value={form.duties}
                  onChange={(e) => update('duties', e.target.value)}
                  rows={4}
                  placeholder="What should the talent do? Click 'Generate with AI' to auto-create…"
                  data-testid="admin-duties-textarea"
                />
              </div>

              {/* Curated Dress Code Selection — mirrors client booking UX */}
              <div className="space-y-3 md:col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-200" data-testid="admin-dress-code-block">
                <Label className="text-gray-900 font-semibold">Dress Code</Label>
                {curatedDressCodes.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {curatedDressCodes.map((dc) => (
                      <div
                        key={dc.id}
                        className={`relative cursor-pointer rounded-lg border-2 overflow-hidden transition-all ${
                          selectedDressCode?.id === dc.id
                            ? 'border-cyan-500 ring-2 ring-cyan-200'
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                        onClick={() => setSelectedDressCode(selectedDressCode?.id === dc.id ? null : dc)}
                        data-testid={`admin-dress-code-${dc.id}`}
                      >
                        <img src={dc.image_url} alt={dc.name} className="w-full h-32 object-cover" />
                        <div className="p-2 bg-white">
                          <p className="text-xs font-semibold text-gray-900 truncate">{dc.name}</p>
                        </div>
                        {selectedDressCode?.id === dc.id && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">Loading curated dress code presets…</p>
                )}
                {selectedDressCode && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                    <p className="text-sm font-medium text-blue-900">{selectedDressCode.name}</p>
                    <p className="text-xs text-blue-700 mt-1">{selectedDressCode.description}</p>
                    <ul className="mt-2 space-y-1">
                      {(selectedDressCode.checklist || []).map((item, i) => (
                        <li key={i} className="flex items-center gap-1.5 text-xs text-blue-800">
                          <CheckCircle className="w-3 h-3 text-blue-500 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="pt-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-gray-500">Custom dress code (optional, plain text)</Label>
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={generateAIDressCode}
                      disabled={aiDressGenerating || !form.role_id}
                      data-testid="admin-generate-dress-code-btn"
                    >
                      {aiDressGenerating ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Generating…</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Generate with AI</>}
                    </Button>
                  </div>
                  <Textarea
                    value={form.dress_code}
                    onChange={(e) => update('dress_code', e.target.value)}
                    rows={3}
                    placeholder="e.g. All-black professional attire, closed-toe shoes, iEvntür name tag"
                    data-testid="admin-dress-code-input"
                  />
                </div>
              </div>

              {/* Attire Restrictions */}
              <div className="space-y-3 md:col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-200" data-testid="admin-attire-block">
                <Label className="text-gray-900 font-semibold">Attire Restrictions</Label>
                <div className="grid grid-cols-2 gap-2">
                  {curatedAttireRestrictions.map((r) => (
                    <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`admin-attire-${r.id}`}>
                      <input
                        type="checkbox"
                        checked={attireRestrictions.includes(r.label)}
                        onChange={(e) => {
                          if (e.target.checked) setAttireRestrictions([...attireRestrictions, r.label]);
                          else setAttireRestrictions(attireRestrictions.filter((x) => x !== r.label));
                        }}
                        className="rounded border-gray-300 text-cyan-500"
                      />
                      <span className="text-gray-700">{r.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Grooming Requirements */}
              <div className="space-y-3 md:col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-200" data-testid="admin-grooming-block">
                <Label className="text-gray-900 font-semibold">Grooming Requirements</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {curatedGroomingReqs.map((r) => (
                    <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`admin-grooming-${r.id}`}>
                      <input
                        type="checkbox"
                        checked={groomingReqs.includes(r.label)}
                        onChange={(e) => {
                          if (e.target.checked) setGroomingReqs([...groomingReqs, r.label]);
                          else setGroomingReqs(groomingReqs.filter((x) => x !== r.label));
                        }}
                        className="rounded border-gray-300 text-cyan-500"
                      />
                      <span className="text-gray-700">{r.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Custom Dress Code Image Uploader — Iter-133q */}
              <div className="space-y-3 md:col-span-2 p-4 bg-white rounded-lg border border-dashed border-gray-300" data-testid="admin-dress-code-upload-block">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-900 font-semibold">Custom Dress Code Images <span className="text-xs text-gray-500 font-normal">(client-provided or brand-specific)</span></Label>
                  <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-cyan-700 hover:text-cyan-900">
                    {uploadingDress ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Uploading…</> : '+ Upload image'}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => { uploadCustomImage('dress_code', e.target.files); e.target.value = ''; }}
                      data-testid="admin-upload-dress-image-input"
                      disabled={uploadingDress}
                    />
                  </label>
                </div>
                {customDressImages.length === 0 ? (
                  <p className="text-xs text-gray-500">No custom images. Max 5MB each (JPEG / PNG / WEBP).</p>
                ) : (
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                    {customDressImages.map((url, i) => (
                      <div key={url} className="relative group" data-testid={`admin-dress-image-${i}`}>
                        <img src={url} alt={`dress-code-${i}`} className="w-full h-24 object-cover rounded-md border" />
                        <button
                          type="button"
                          onClick={() => removeCustomImage('dress_code', url)}
                          className="absolute top-1 right-1 bg-red-600 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`admin-remove-dress-image-${i}`}
                        >✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Grooming Image Uploader — Iter-133q */}
              <div className="space-y-3 md:col-span-2 p-4 bg-white rounded-lg border border-dashed border-gray-300" data-testid="admin-grooming-upload-block">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-900 font-semibold">Grooming Reference Images <span className="text-xs text-gray-500 font-normal">(hair, makeup, nails examples)</span></Label>
                  <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-cyan-700 hover:text-cyan-900">
                    {uploadingGrooming ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Uploading…</> : '+ Upload image'}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => { uploadCustomImage('grooming', e.target.files); e.target.value = ''; }}
                      data-testid="admin-upload-grooming-image-input"
                      disabled={uploadingGrooming}
                    />
                  </label>
                </div>
                {customGroomingImages.length === 0 ? (
                  <p className="text-xs text-gray-500">No grooming images. Max 5MB each.</p>
                ) : (
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                    {customGroomingImages.map((url, i) => (
                      <div key={url} className="relative group" data-testid={`admin-grooming-image-${i}`}>
                        <img src={url} alt={`grooming-${i}`} className="w-full h-24 object-cover rounded-md border" />
                        <button
                          type="button"
                          onClick={() => removeCustomImage('grooming', url)}
                          className="absolute top-1 right-1 bg-red-600 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`admin-remove-grooming-image-${i}`}
                        >✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Booking Attachments Uploader — Iter-133q */}
              <div className="space-y-3 md:col-span-2 p-4 bg-white rounded-lg border border-dashed border-gray-300" data-testid="admin-attachments-block">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-900 font-semibold">Client Documents & Attachments <span className="text-xs text-gray-500 font-normal">(run of show, decks, talking points, brand guidelines)</span></Label>
                  <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-cyan-700 hover:text-cyan-900">
                    + Attach file
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,image/*,text/*"
                      className="hidden"
                      onChange={(e) => { queueAttachment(e.target.files); e.target.value = ''; }}
                      data-testid="admin-upload-attachment-input"
                    />
                  </label>
                </div>
                {queuedAttachments.length === 0 ? (
                  <p className="text-xs text-gray-500">
                    No attachments yet. PDFs, images, docs up to 15MB. Will be uploaded after the shift is created.
                  </p>
                ) : (
                  <ul className="divide-y divide-gray-100 border rounded-md">
                    {queuedAttachments.map((att, i) => (
                      <li key={i} className="flex items-center gap-3 p-2 text-sm" data-testid={`admin-attachment-queue-${i}`}>
                        <span className="flex-1 truncate text-gray-800">{att.file.name}</span>
                        <span className="text-xs text-gray-500">{(att.file.size / 1024).toFixed(0)} KB</span>
                        <label className="flex items-center gap-1 text-xs text-gray-600">
                          <input type="checkbox" checked={att.visible_to_client} onChange={() => toggleAttachmentVis(i, 'visible_to_client')} /> Client
                        </label>
                        <label className="flex items-center gap-1 text-xs text-gray-600">
                          <input type="checkbox" checked={att.visible_to_talent} onChange={() => toggleAttachmentVis(i, 'visible_to_talent')} /> Talent
                        </label>
                        <button
                          type="button"
                          onClick={() => removeQueuedAttachment(i)}
                          className="text-red-600 hover:text-red-800 text-xs"
                          data-testid={`admin-remove-attachment-${i}`}
                        >Remove</button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[11px] text-gray-500 mt-1">Visibility: unchecking "Client"/"Talent" hides the file from that audience. Unbooked talent never see attachments regardless of setting.</p>
              </div>

              {/* Send Home Warning */}
              <div className="md:col-span-2 flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="admin-send-home-block">
                <Switch
                  checked={sendHomeWarning}
                  onCheckedChange={setSendHomeWarning}
                  data-testid="admin-send-home-warning-switch"
                />
                <div>
                  <p className="text-sm font-medium text-amber-900">Send Home Warning</p>
                  <p className="text-xs text-amber-700">Display: "This partner may send you home if these requirements are not met."</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Confirm Within (hours)</Label>
                <Input
                  type="number" min="1" max="168"
                  value={form.confirmation_required_hours}
                  onChange={(e) => update('confirmation_required_hours', parseInt(e.target.value || '24', 10))}
                  data-testid="confirm-hours-input"
                />
                <p className="text-xs text-gray-400">Talent must confirm booking within this window (default 24h).</p>
              </div>
              <div className="space-y-2">
                <Label>Cancellation Cutoff (hours before shift)</Label>
                <Input
                  type="number" min="0" max="168"
                  value={form.cancellation_cutoff_hours}
                  onChange={(e) => update('cancellation_cutoff_hours', parseInt(e.target.value || '24', 10))}
                  data-testid="cancel-hours-input"
                />
                <p className="text-xs text-gray-400">Late cancels (after cutoff) flag the talent for reliability tracking.</p>
              </div>
              <div className="space-y-2 md:col-span-2">
                <div className="flex items-center justify-between">
                  <Label>Important Notes / Instructions <span className="text-xs text-gray-400">(arrival, parking, onsite contact, brand talking points)</span></Label>
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={generateAIImportantNotes}
                    disabled={aiNotesGenerating || !form.role_id}
                    data-testid="admin-generate-important-notes-btn"
                  >
                    {aiNotesGenerating ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Generating…</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Generate with AI</>}
                  </Button>
                </div>
                <Textarea
                  value={form.important_notes}
                  onChange={(e) => update('important_notes', e.target.value)}
                  rows={4}
                  placeholder="Arrival: enter through back loading dock.&#10;Parking: validated next door.&#10;Onsite Contact: Sarah – 305-555-0199.&#10;Brand Talking Points: highlight the new product launch..."
                  data-testid="important-notes-input"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Additional Notes</Label>
                <Textarea value={form.additional_notes} onChange={(e) => update('additional_notes', e.target.value)} rows={2} placeholder="Other reminders, etc." />
              </div>

              {/* iter417/iter418 — Onsite / Arrival Details (Evie reads these) */}
              <div className="space-y-2 md:col-span-2 bg-blue-50/50 border border-blue-200 rounded-lg p-3" data-testid="onsite-details-block">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  <Label className="text-sm font-semibold">Onsite / Arrival Details</Label>
                </div>
                <p className="text-xs text-gray-500">
                  Booth #, reporting location, parking and maps. Evie reads these to answer "where do I go?" for booked talent.
                </p>
                <OnsiteDetailsFields
                  idPrefix="admin-create-onsite"
                  value={form}
                  onChange={(k, v) => update(k, v)}
                />
              </div>

              {/* Visibility scope — default local_70mi */}
              <div className="space-y-2 md:col-span-2 bg-amber-50/60 border border-amber-200 rounded-lg p-3" data-testid="visibility-block">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label className="text-sm font-semibold">Who can see this job?</Label>

                  {/* LIVE AUDIENCE COUNTER */}
                  <div
                    className="flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-full bg-white border border-amber-300 text-amber-800 shadow-sm min-w-[200px] justify-center"
                    data-testid="audience-live-counter"
                  >
                    <Eye className="w-4 h-4 text-amber-600" />
                    {audience.loading ? (
                      <span className="flex items-center gap-1.5 text-gray-500">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Calculating…
                      </span>
                    ) : audience.error === 'needs_address' ? (
                      <span className="text-gray-500 font-normal">Fill venue address to preview</span>
                    ) : audience.count == null ? (
                      <span className="text-gray-500 font-normal">Fill city / state to preview</span>
                    ) : (
                      <span data-testid="audience-count-value">
                        <span className="text-amber-700 font-bold">{audience.count.toLocaleString()}</span>{' '}
                        <span className="font-normal text-gray-700">talent will see this job</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Nearest-sample strip — only shown when we have data */}
                {audience.count != null && audience.count > 0 && audience.sample.length > 0 && (
                  <div
                    className="flex items-center gap-1.5 flex-wrap text-[11px] text-gray-600 pt-1"
                    data-testid="audience-sample-nearest"
                  >
                    <span className="text-gray-400 font-medium uppercase tracking-wide">Nearest:</span>
                    {audience.sample.map((s, i) => (
                      <span
                        key={`${s.city || 'x'}-${i}`}
                        className="bg-white border border-amber-200 rounded-full px-2 py-0.5"
                      >
                        {s.city || '?'}{s.distance_miles != null ? ` · ${s.distance_miles} mi` : ''}
                      </span>
                    ))}
                  </div>
                )}

                <p className="text-xs text-gray-500">
                  Default <b>Local (70 miles)</b>. Only talent within this radius of the venue will
                  see the listing and receive notifications. Broader visibility should be
                  intentional.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-1">
                  {[
                    { v: 'local_70mi', lbl: 'Local (70 mi)' },
                    { v: 'custom_radius', lbl: 'Custom radius' },
                    { v: 'state', lbl: 'Statewide' },
                    { v: 'national', lbl: 'Nationwide' },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`visibility-scope-${opt.v}`}
                      onClick={() => update('visibility_scope', opt.v)}
                      className={`text-xs py-1.5 px-2 rounded-md border transition ${
                        form.visibility_scope === opt.v
                          ? 'bg-amber-600 text-white border-amber-600'
                          : 'bg-white hover:bg-amber-100 border-amber-300'
                      }`}
                    >
                      {opt.lbl}
                    </button>
                  ))}
                </div>
                {form.visibility_scope === 'custom_radius' && (
                  <div className="flex items-center gap-2 pt-2">
                    <Label className="text-xs">Radius (miles):</Label>
                    <Input
                      type="number" min="5" max="500"
                      className="w-24 h-8"
                      value={form.visibility_radius_miles}
                      onChange={(e) => update('visibility_radius_miles', parseInt(e.target.value || '70', 10))}
                      data-testid="visibility-radius-input"
                    />
                  </div>
                )}
                {form.visibility_scope === 'national' && (
                  <p className="text-xs text-rose-600 pt-1 flex items-center gap-1">
                    <span>⚠</span> Nationwide broadcast — every active talent will see and can be notified about this job. Use sparingly.
                  </p>
                )}
              </div>

              {/* Iter-133 — Auto-Close toggle (default ON) */}
              <div
                className="md:col-span-2 bg-emerald-50/60 border border-emerald-200 rounded-lg p-3 flex items-start justify-between gap-4"
                data-testid="auto-close-block"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-semibold">Auto-Close When Clocked Out</Label>
                    <span className="text-[10px] uppercase tracking-wide text-emerald-700 bg-white border border-emerald-200 rounded-full px-1.5 py-0.5">Iter-133</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1 max-w-xl">
                    When every primary talent has clocked out (and no clock-out hygiene anomaly is present),
                    the shift will auto-close — admin no longer needs to press "Mark Shift Completed" manually.
                    Recommended ON.
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none shrink-0" data-testid="auto-close-toggle-wrapper">
                  <input
                    type="checkbox"
                    checked={!!form.auto_close_enabled}
                    onChange={(e) => update('auto_close_enabled', e.target.checked)}
                    data-testid="auto-close-toggle"
                    className="w-5 h-5 accent-emerald-600"
                  />
                  <span className={`text-sm font-semibold ${form.auto_close_enabled ? 'text-emerald-700' : 'text-gray-500'}`}>
                    {form.auto_close_enabled ? 'ON' : 'OFF'}
                  </span>
                </label>
              </div>
            </div>

            {/* Live Calculator — admin-only profitability preview */}
            <div className="pt-4 border-t">
              <LiveRateCalculator
                billRate={form.bill_rate}
                talentPayRate={
                  form.talent_pay_rate !== '' && form.talent_pay_rate !== null
                    ? form.talent_pay_rate
                    : (parseFloat(form.bill_rate || 0) * 0.70).toFixed(2)
                }
                hours={(() => {
                  // parse HH:MM → minutes to hours
                  const [sh, sm] = (form.start_time || '0:0').split(':').map(Number);
                  const [eh, em] = (form.end_time || '0:0').split(':').map(Number);
                  let mins = (eh * 60 + em) - (sh * 60 + sm);
                  if (mins < 0) mins += 24 * 60;
                  return +(mins / 60).toFixed(2);
                })()}
                staffCount={form.talent_needed}
                flatPay={form.pay_type === 'flat' ? form.flat_pay : null}
              />
            </div>

            {/* Iter-297 — Multi-role event editor moved up to sit
                between the Primary Role section and Backup Staffing. */}

            <div className="flex justify-end pt-2">
              <Button
                onClick={submit}
                disabled={submitting || !selectedClient}
                className="bg-cyan-500 hover:bg-cyan-600 text-white px-8"
                data-testid="submit-shift-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating…</>
                ) : (
                  extraRoleSlots.length > 0
                    ? `Create Event (${extraRoleSlots.length + 1} roles)`
                    : 'Create Job'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Iter-297 — Add New Company modal (stub creation, no invite). */}
      <AddNewCompanyModal
        open={addCompanyOpen}
        onClose={() => setAddCompanyOpen(false)}
        onCreated={(newClient) => {
          setSelectedClient(newClient);
          // Prepend to local search results so the choice stays visible
          // if the admin re-opens the picker (e.g. via "Change").
          setClientResults((prev) => [newClient, ...prev]);
        }}
      />
    </div>
  );
};

export default AdminCreateShift;
